import React, { memo, ReactNode } from 'react';
import cls from 'classnames';

import WidgetHeader from './blocks/WidgetHeader/WidgetHeader';
import WidgetSlider from './blocks/WidgetSlider';
import useActivitiesSearch, { SearchParams } from './effects/useActivitiesSearch';

import qs from 'qs';
import _omit from 'lodash/omit';
import { FirstLikeModalProps } from '@ott/activity-card';

export const ACTIVITIES_TEMPLATE =
  '{count, plural, one {экскурсия} few {экскурсии} many {экскурсий} other {экскурсий}}';

type UrlParams = {
  byInterest?: string | number;
  end_date?: string;
  id: number;
  s: boolean; // для nginx, чтобы отличать от seo
  start_date?: string;
};

export type WidgetProps = {
  className?: string;
  /**
   * Показывать ли карточку, ведущую на серп, последним слайдом.
   * */
  isSearchCardVisible?: boolean;
  /**
   * Показывать ли ссылку "Смотреть все", ведущую на серп, в шапке виджета справа.
   * */
  isSearchLinkVisible?: boolean;
  /**
   * Показывать ли подзаголовок с кол-вом экскурсий и датами.
   * */
  isSubtitleVisible?: boolean;
  /**
   * Метод, который открывает модал авторизации на вертикали.
   * Нужен для кнопки "Войти" в модале первого лайка.
   * */
  openAuth?: FirstLikeModalProps['openAuth'];
  /**
   * Функция для генерации ссылки на экскурсию. Обычно нужна в контексте приложения экскурсий.
   * */
  prepareCustomActivityLink?: (activityId: number, params: Omit<UrlParams, 'byInterest'>) => string;
  /**
   * Функция для генерации ссылки на серп. Обычно нужна в контексте приложения экскурсий.
   * */
  prepareCustomSearchLink?: (params: UrlParams) => string;
  /**
   * Параметры поиска экскурсий (в таблицу вынесен отдельным типом).
   * */
  searchParams: SearchParams;
  /**
   * При клике по ссылке/карточке открываем новую вкладку.
   * */
  shouldOpenInNewTab?: boolean;
  /**
   * Приоритизировать при сортировке экскурсии с лайками.
   * */
  shouldPrioritiseDiscounted: boolean;
  /**
   * Включена ли возможность ставить лайки в карточках виджета.
   * */
  shouldShowLike?: boolean;
  /**
   * Использовать ли внешние ссылки (вместо Link роутера). Обычно нужно отключать внутри приложения экскурсий.
   * */
  shouldUseExternalLinks?: boolean;
  specificLocators?: string;
  /**
   * Шаблон для переопределения подзаголовка. Обычно используется для уточнения категории экскурсий.
   * */
  subtitleActivitiesTemplate?: string;
  /**
   * Идентификатор виджета. Нужен в случае, если на странице несколько виджетов.
   * Например, `widget-1`, `widget-2`, ...
   * Для остальных случаев достаточно `trackPage`.
   * */
  trackId?: string;
  /**
   * Вертикаль и страница для статистики. Например, `activities-details`, `hotels-order`, ...
   * */
  trackPage: string;
  /**
   * Основной заголовок.
   * */
  title: ReactNode;
};

const ActivitiesWidget = ({
  className,
  isSearchCardVisible = true,
  isSearchLinkVisible = true,
  isSubtitleVisible = true,
  openAuth,
  prepareCustomActivityLink,
  prepareCustomSearchLink,
  searchParams,
  shouldOpenInNewTab = false,
  shouldPrioritiseDiscounted = true,
  shouldShowLike = true,
  shouldUseExternalLinks = true,
  specificLocators,
  subtitleActivitiesTemplate = ACTIVITIES_TEMPLATE,
  title = 'Экскурсии',
  trackId,
  trackPage,
}: WidgetProps) => {
  const { isLoading, hasError, activities, meta, updateActivity } = useActivitiesSearch(
    searchParams,
    {
      shouldPrioritiseLiked: shouldShowLike,
      shouldPrioritiseDiscounted,
      trackId,
      trackPage,
    }
  );

  if (hasError) {
    console.error('[ActivitiesWidget]: ошибка при поиске экскурсий');

    return null;
  }

  const { startDate, endDate, localityId } = meta;

  const urlParams = {
    id: localityId,
    start_date: startDate,
    end_date: endDate,
    byInterest: searchParams.categories,
    s: true,
  };

  const prepareSearchLink = () => {
    if (prepareCustomSearchLink) {
      return prepareCustomSearchLink(urlParams);
    }

    return `/search?${qs.stringify(urlParams)}`;
  };

  const prepareActivityLink = (activityId: number) => {
    const activityUrlParams = _omit(urlParams, 'byInterest');

    if (prepareCustomActivityLink) {
      return prepareCustomActivityLink(activityId, activityUrlParams);
    }

    return `/activities/?${activityId}/${qs.stringify(activityUrlParams)}`;
  };

  return (
    <div className={cls(className)} data-locator={cls('activities-widget', specificLocators)}>
      <WidgetHeader
        isLoading={isLoading}
        isSearchLinkVisible={isSearchLinkVisible}
        isSubtitleVisible={isSubtitleVisible}
        meta={meta}
        prepareSearchLink={prepareSearchLink}
        shouldOpenInNewTab={shouldOpenInNewTab}
        shouldUseExternalLinks={shouldUseExternalLinks}
        subtitleActivitiesTemplate={subtitleActivitiesTemplate}
        title={title}
        trackId={trackId}
        trackPage={trackPage}
      />

      <WidgetSlider
        activities={activities}
        isLoading={isLoading}
        isSearchCardVisible={isSearchCardVisible}
        meta={meta}
        openAuth={openAuth}
        prepareActivityLink={prepareActivityLink}
        prepareSearchLink={prepareSearchLink}
        shouldOpenInNewTab={shouldOpenInNewTab}
        shouldShowLike={shouldShowLike}
        shouldUseExternalLinks={shouldUseExternalLinks}
        trackId={trackId}
        trackPage={trackPage}
        updateActivity={updateActivity}
      />
    </div>
  );
};

export default memo(ActivitiesWidget);
